import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import WizardShell from '../ui/wizard-shell';
import CommunicationComposer, {
  type CommunicationRecipient,
  type GroupDraftState,
  type RecipientGroup,
} from '../communications/CommunicationComposer';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem } from '../ui/wizard-layout';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { erstesPaket, getEnrollmentForTrial, getEnrollmentPaymentReadiness, type EnrollmentDTO, type EnrollmentPaymentReadiness } from '../../lib/enrollments';
import { stundensatzAus } from '../../lib/honorar';
import { type TrialLessonDTO } from '../../lib/trials';
import { masterdataApi, type UnterrichtsformItem } from '../../lib/courses';
import { api, communicationsApi, historyTemplatesApi, type CommunicationsRecipient, type RenderHistoryTemplateResponse } from '../../lib/api';
import { confirmOrder } from '../../lib/orders';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { computePlannedPeriodRange } from '../../lib/plannedPeriod';
import { einwilligungenText } from '../../lib/einwilligungen';
import { useAuth } from '../auth/use-auth';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  onConfirmed?: () => Promise<void> | void;
  /**
   * Migration-Modus: Wenn gesetzt, wird das Enrollment direkt per ID geladen
   * (statt ueber /api/trials/{id}/enrollments) — Migration-Cases haben keine
   * Probestunde. Der `trial`-Prop ist dann ein synthetischer DTO, den der
   * Migration-Parent aus dem Case-Snapshot baut.
   */
  enrollmentIdOverride?: number;
};

type UnterrichtsformArtLite = {
  id: number;
  currency?: string | null;
  startpaket_enabled?: boolean;
  startpaket_units?: number | null;
  startpaket_price_cents?: number | null;
  startpaket_signup_fee_cents?: number | null;
};

type TeacherPersonPreview = {
  teacher?: {
    artRates?: Array<{
      unterrichtsform_art_id?: number | string;
      art_id?: number | string;
      rate?: number | string;
    }>;
  } | null;
  artRates?: Array<{
    unterrichtsform_art_id?: number | string;
    art_id?: number | string;
    rate?: number | string;
  }>;
};

function toDateLabel(value?: string | null): string {
  if (!value) return '-';
  try {
    return format(parseISO(value), 'dd.MM.yyyy', { locale: de });
  } catch {
    return value;
  }
}

function toTimeLabel(value?: string | null): string {
  if (!value) return '-';
  return value.slice(0, 5);
}

function weekdayForTermin(weekday?: number | null, dateValue?: string | null): string {
  const names = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
  if (typeof weekday === 'number' && weekday >= 1 && weekday <= 7) {
    return `${names[weekday - 1]}s`;
  }
  if (dateValue) {
    try {
      const d = parseISO(dateValue);
      const day = d.getDay(); // 0=Sonntag, 1=Montag ...
      const mondayFirst = day === 0 ? 7 : day;
      return `${names[mondayFirst - 1]}s`;
    } catch {
      return '';
    }
  }
  return '';
}

function addMinutesToTime(timeValue?: string | null, durationMin?: number | null): string | null {
  const base = String(timeValue || '').trim();
  if (!base || !base.includes(':') || !durationMin || durationMin <= 0) return null;
  const hh = Number(base.slice(0, 2));
  const mm = Number(base.slice(3, 5));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  const total = (hh * 60 + mm + durationMin) % (24 * 60);
  const outH = Math.floor(total / 60).toString().padStart(2, '0');
  const outM = (total % 60).toString().padStart(2, '0');
  return `${outH}:${outM}`;
}

function toMoney(cents?: number | null, currency = 'EUR'): string {
  if (typeof cents !== 'number') return '-';
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

function durationMinutesFromTrial(trial: TrialLessonDTO): number | null {
  const start = String(trial.startTime || '').slice(0, 5);
  const end = String(trial.endTime || '').slice(0, 5);
  if (!start || !end || !start.includes(':') || !end.includes(':')) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  const diff = endMin - startMin;
  return diff > 0 ? diff : null;
}

function paymentProviderLabel(provider?: string | null): string {
  const value = String(provider || '').toLowerCase();
  if (!value) return 'Nicht festgelegt';
  if (value === 'stripe') return 'Stripe';
  return provider || 'Nicht festgelegt';
}

function paymentMethodLabel(paymentMethodId?: string | null): string {
  const raw = String(paymentMethodId || '').trim();
  if (!raw) return 'Keine Zahlungsart hinterlegt';
  if (raw.length <= 10) return 'Zahlungsart hinterlegt';
  return `Zahlungsart hinterlegt (${raw.slice(0, 6)}…${raw.slice(-4)})`;
}

function paymentStatusPill(
  paymentStatus?: string | null,
  chargeState?: string | null
): { label: string; className: string } {
  const p = String(paymentStatus || '').toLowerCase();
  const c = String(chargeState || '').toLowerCase();

  if (p === 'succeeded' || p === 'paid' || c === 'charged') {
    return {
      label: 'Zahlung aktiv',
      className: 'border-green-200 bg-green-50 text-green-700',
    };
  }
  if (p === 'pending' || p === 'processing' || c === 'scheduled' || c === 'retry') {
    return {
      label: 'Zahlung geplant',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
    };
  }
  if (p === 'failed' || p === 'canceled' || p === 'requires_action' || c === 'failed') {
    return {
      label: 'Zahlung fehlerhaft',
      className: 'border-red-200 bg-red-50 text-red-700',
    };
  }

  return {
    label: 'Zahlung offen',
    className: 'border-slate-200 bg-slate-50 text-slate-700',
  };
}

function isPaymentStatusGreen(paymentStatus?: string | null, chargeState?: string | null): boolean {
  const p = String(paymentStatus || '').toLowerCase();
  const c = String(chargeState || '').toLowerCase();
  return p === 'succeeded' || p === 'paid' || c === 'charged';
}

function channelPillClass(active: boolean): string {
  return cn(
    'inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-medium',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-muted bg-muted/70 text-muted-foreground',
  );
}

const HISTORY_TEMPLATE_KEY = 'order.confirmed.history';

type ResolvedContact = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
};

function weekdayNameFromNumber(weekday?: number | null): string {
  const names = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
  if (typeof weekday === 'number' && weekday >= 1 && weekday <= 7) {
    return names[weekday - 1];
  }
  return '';
}

export default function ConfirmOrderWizardModal({ open, onOpenChange, trial, onConfirmed, enrollmentIdOverride }: Props) {
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enrollment, setEnrollment] = useState<EnrollmentDTO | null>(null);
  const [loadingEnrollment, setLoadingEnrollment] = useState(false);
  const [unterrichtsformen, setUnterrichtsformen] = useState<UnterrichtsformItem[]>([]);
  const [unterrichtsformArten, setUnterrichtsformArten] = useState<UnterrichtsformArtLite[]>([]);
  const [teacherPerson, setTeacherPerson] = useState<TeacherPersonPreview | null>(null);
  const [followupPackageUnits, setFollowupPackageUnits] = useState<number | null>(null);
  const [paymentReadiness, setPaymentReadiness] = useState<EnrollmentPaymentReadiness | null>(null);
  const [loadingPaymentReadiness, setLoadingPaymentReadiness] = useState(false);
  const [draftStateByGroup, setDraftStateByGroup] = useState<Record<string, GroupDraftState>>({});
  const entwuerfe = () => {
    const aus = (key: string) => {
      const email = draftStateByGroup[key]?.email;
      if (!email) return undefined;
      return { subject: email.subjectTpl || '', html: email.htmlTpl || '', text: email.textTpl || '' };
    };
    return { student: aus('student'), teacher: aus('teacher') };
  };
  const [resolvedContacts, setResolvedContacts] = useState<Record<string, ResolvedContact>>({});
  const [historyHtml, setHistoryHtml] = useState('');
  const [selectedPackage, setSelectedPackage] = useState<{ units?: number | null } | null>(null);
  const [paketListe, setPaketListe] = useState<Array<{ id: string; units?: number | null; special?: boolean }>>([]);
  const lastDraftSignatureRef = React.useRef<string>('');

  const orderId = (trial.order?.id || 0) as number;
  // Die Probestunde liefert standortId; ältere Aufrufer standort_id.
  const standortId = Number((trial as any)?.standortId ?? (trial as any)?.standort_id ?? 0) || 0;

  React.useEffect(() => {
    if (!open) return;
    setStep(1);
    setDraftStateByGroup({});
    setResolvedContacts({});
    setHistoryHtml('');
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    setLoadingEnrollment(true);
    // Migration-Modus: Enrollment direkt per ID. Sonst: ueber die Probestunde.
    const enrollmentLoader = enrollmentIdOverride && enrollmentIdOverride > 0
      ? api.get<EnrollmentDTO>(`/api/enrollments/${enrollmentIdOverride}`).catch(() => null)
      : getEnrollmentForTrial(trial.id).catch(() => null);
    Promise.all([
      enrollmentLoader,
      masterdataApi.unterrichtsformen().catch(() => [] as UnterrichtsformItem[]),
      api.get<UnterrichtsformArtLite[]>('/api/unterrichtsform-arten').catch(() => [] as UnterrichtsformArtLite[]),
    ])
      .then(async ([enrollmentRes, forms, arten]) => {
        setEnrollment(enrollmentRes);
        setPaymentReadiness(null);
        setUnterrichtsformen(forms || []);
        setUnterrichtsformArten(Array.isArray(arten) ? arten : []);
        if (enrollmentRes?.id) {
          setLoadingPaymentReadiness(true);
          try {
            const readiness = await getEnrollmentPaymentReadiness(enrollmentRes.id);
            setPaymentReadiness(readiness);
          } catch {
            setPaymentReadiness(null);
          } finally {
            setLoadingPaymentReadiness(false);
          }
        } else {
          setLoadingPaymentReadiness(false);
        }
        // Termine des gewählten Pakets — wie im Angebot; die Unterrichtsform-Art kennt
        // sie nur, wenn dort Startpaket-Einheiten gepflegt sind.
        const previewDuration = enrollmentRes?.durationMin || durationMinutesFromTrial(trial);
        if (enrollmentRes?.unterrichtsformId && previewDuration) {
          try {
            const pricing = await api.get<{
              packages?: Array<{ id: string; units?: number | null; special?: boolean }>;
            }>(
              `/api/enrollments/preview-packages?unterrichtsformId=${encodeURIComponent(
                String(enrollmentRes.unterrichtsformId)
              )}&duration=${encodeURIComponent(String(previewDuration))}`
            );
            const all = pricing?.packages || [];
            setPaketListe(all);
            const wanted = String(enrollmentRes?.contingentPackageId || '').trim();
            setSelectedPackage(
              (wanted ? all.find((pkg) => String(pkg.id) === wanted) : null)
              ?? (enrollmentRes?.startpaketSelected ? all.find((pkg) => pkg.special) : null)
              ?? null
            );
          } catch {
            setSelectedPackage(null);
          }
        } else {
          setSelectedPackage(null);
        }
        const teacherWpUserId = Number.parseInt(String(trial.teacherId || ''), 10) || 0;
        if (teacherWpUserId > 0) {
          try {
            const person = await api.get<TeacherPersonPreview>(`/api/personen/${teacherWpUserId}`);
            setTeacherPerson(person || null);
          } catch {
            setTeacherPerson(null);
          }
        } else {
          setTeacherPerson(null);
        }
      })
      .catch(() => {
        setEnrollment(null);
        setSelectedPackage(null);
        setPaketListe([]);
        setPaymentReadiness(null);
        setLoadingPaymentReadiness(false);
        setUnterrichtsformen([]);
        setUnterrichtsformArten([]);
        setTeacherPerson(null);
      })
      .finally(() => setLoadingEnrollment(false));
  }, [open, trial.id, trial.teacherId, enrollmentIdOverride]);

  React.useEffect(() => {
    if (!open || !enrollment?.followupPackageId || !enrollment.unterrichtsformId) {
      setFollowupPackageUnits(null);
      return;
    }
    if (typeof enrollment.packageUnits === 'number' && enrollment.packageUnits > 0) {
      setFollowupPackageUnits(null);
      return;
    }
    let cancelled = false;
    api.get<Array<{ id?: string | number; units?: number }>>(
      `/api/unterrichtsformen/${encodeURIComponent(String(enrollment.unterrichtsformId))}/packages`,
    )
      .then((packages) => {
        if (cancelled) return;
        const hit = (packages || []).find((pkg) => String(pkg.id) === String(enrollment.followupPackageId));
        setFollowupPackageUnits(typeof hit?.units === 'number' && hit.units > 0 ? hit.units : null);
      })
      .catch(() => {
        if (!cancelled) setFollowupPackageUnits(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, enrollment?.followupPackageId, enrollment?.unterrichtsformId, enrollment?.packageUnits]);

  const recipientGroups: RecipientGroup[] = useMemo(() => {
    if (!standortId) return [];

    const groups: RecipientGroup[] = [];

    const subprofileId = String((trial as any)?.subprofileId || enrollment?.subprofileId || '').trim();
    if (subprofileId.includes('-')) {
      const [wpStr, subStr] = subprofileId.split('-', 2);
      const wpUserId = Number.parseInt(wpStr, 10);
      const subIndex = Number.parseInt(subStr || '', 10);
      if (wpUserId > 0) {
        const vpId = `person:${wpUserId}:vp`;
        const vpResolved = resolvedContacts[`person:${wpUserId}:`] || resolvedContacts[vpId];
        const recipients: CommunicationRecipient[] = [{
          id: vpId,
          type: 'person',
          wpUserId,
          subIndex: null,
          name: vpResolved?.name || trial.studentName || 'Vertragspartner',
        }];

        if (!Number.isNaN(subIndex) && subIndex > 0) {
          const studentId = `person:${wpUserId}:${subIndex}`;
          const studentResolved = resolvedContacts[studentId];
          const vpEmail = (vpResolved?.email || '').trim().toLowerCase();
          const studentEmail = (studentResolved?.email || '').trim().toLowerCase();
          if (studentEmail !== '' && studentEmail !== vpEmail) {
            recipients.push({
              id: studentId,
              type: 'person',
              wpUserId,
              subIndex,
              name: studentResolved?.name || trial.studentName || 'Schüler',
            });
          }
        }

        groups.push({
          key: 'student',
          label: recipients.length > 1 ? 'Vertragspartner & Schüler' : 'Kunde',
          recipients,
          templateKeys: { email: 'course.confirmed.student' },
          vars: {
            studentName: trial.studentName || 'Schüler',
            teacherName: trial.teacherName || 'Lehrer',
            startDate: enrollment?.startDate || trial.date,
            startTime: toTimeLabel(enrollment?.startTime || trial.startTime),
          },
          defaultSelectedChannels: { email: true, sms: false },
        });
      }
    }

    const teacherWpId = Number.parseInt(String((trial as any)?.teacherId || ''), 10);
    if (teacherWpId > 0) {
      const teacherId = `person:${teacherWpId}:`;
      const teacherResolved = resolvedContacts[teacherId];
      const recipients: CommunicationRecipient[] = [{
        id: teacherId,
        type: 'person',
        wpUserId: teacherWpId,
        name: teacherResolved?.name || trial.teacherName || 'Lehrer',
      }];
      groups.push({
        key: 'teacher',
        label: 'Lehrer',
        recipients,
        templateKeys: { email: 'course.confirmed.teacher' },
        vars: {
          studentName: trial.studentName || 'Schüler',
          teacherName: trial.teacherName || 'Lehrer',
          startDate: enrollment?.startDate || trial.date,
          startTime: toTimeLabel(enrollment?.startTime || trial.startTime),
        },
        defaultSelectedChannels: { email: true, sms: false },
      });
    }

    return groups;
  }, [standortId, trial, enrollment, resolvedContacts]);

  React.useEffect(() => {
    if (!open || !standortId) return;

    const subprofileId = String((trial as any)?.subprofileId || enrollment?.subprofileId || '').trim();
    const backendRecipients: CommunicationsRecipient[] = [];

    if (subprofileId.includes('-')) {
      const [wpStr, subStr] = subprofileId.split('-', 2);
      const wpUserId = Number.parseInt(wpStr, 10);
      const subIndex = Number.parseInt(subStr || '', 10);
      if (wpUserId > 0) {
        backendRecipients.push({ type: 'person', wpUserId, subIndex: null });
        if (!Number.isNaN(subIndex) && subIndex > 0) {
          backendRecipients.push({ type: 'person', wpUserId, subIndex });
        }
      }
    }

    const teacherWpId = Number.parseInt(String((trial as any)?.teacherId || ''), 10);
    if (teacherWpId > 0) {
      backendRecipients.push({ type: 'person', wpUserId: teacherWpId, subIndex: null });
    }

    if (backendRecipients.length === 0) return;

    let cancelled = false;
    communicationsApi.resolveRecipients({ standortId, recipients: backendRecipients })
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, ResolvedContact> = {};
        for (const item of res.items || []) {
          const id = String(item?.id || '');
          if (!id) continue;
          map[id] = {
            id,
            name: String(item?.name || ''),
            email: item?.email ? String(item.email) : null,
            phone: item?.phone ? String(item.phone) : null,
          };
        }
        setResolvedContacts(map);
      })
      .catch(() => {
        if (!cancelled) setResolvedContacts({});
      });

    return () => {
      cancelled = true;
    };
  }, [open, standortId, trial, enrollment?.subprofileId]);

  const canNext = step === 1 ? !loadingEnrollment && orderId > 0 : step === 2 ? true : false;
  const canConfirm = step === 3 && orderId > 0 && !isSubmitting;
  const currentForm = useMemo(
    () => unterrichtsformen.find((u) => u.id === enrollment?.unterrichtsformId),
    [unterrichtsformen, enrollment?.unterrichtsformId],
  );
  const currentArt = useMemo(() => {
    const artId = Number((currentForm as any)?.art_id || 0);
    if (!artId) return null;
    return unterrichtsformArten.find((a) => a.id === artId) || null;
  }, [currentForm, unterrichtsformArten]);
  const lessonDurationMin = useMemo(
    () => enrollment?.durationMin || durationMinutesFromTrial(trial) || null,
    [enrollment?.durationMin, trial]
  );
  const currency = enrollment?.currency || currentArt?.currency || 'EUR';
  const startpaketSelected = Boolean(enrollment?.startpaketSelected);
  const fallbackStartpaketUnits =
    typeof currentArt?.startpaket_units === 'number' ? currentArt.startpaket_units : null;
  const selectedPackageUnits =
    typeof selectedPackage?.units === 'number' && selectedPackage.units > 0
      ? selectedPackage.units
      : null;
  const resolvedStartpaketUnits =
    selectedPackageUnits
    ?? (typeof enrollment?.startpaketUnits === 'number'
      ? enrollment.startpaketUnits
      : fallbackStartpaketUnits);
  // Was der Kunde gebucht hat, vor dem Schalter „Startpaket hinzufügen“ (T-0284).
  const gebuchtesPaket = erstesPaket(enrollment, paketListe);
  const gebuchteEinheiten =
    typeof gebuchtesPaket?.units === 'number' && gebuchtesPaket.units > 0 ? gebuchtesPaket.units : null;
  const packageUnits = gebuchteEinheiten ?? (startpaketSelected
    ? (resolvedStartpaketUnits ?? (typeof enrollment?.packageUnits === 'number' ? enrollment.packageUnits : null))
    : (typeof enrollment?.packageUnits === 'number' && enrollment.packageUnits > 0
      ? enrollment.packageUnits
      : (selectedPackageUnits ?? followupPackageUnits)));
  const teacherArtRatePerLessonCents = useMemo(() => {
    const artId = Number((currentForm as any)?.art_id || 0);
    if (!teacherPerson || !artId || !lessonDurationMin || lessonDurationMin <= 0) return null;
    const candidates = Array.isArray(teacherPerson.teacher?.artRates)
      ? teacherPerson.teacher?.artRates || []
      : (Array.isArray(teacherPerson.artRates) ? teacherPerson.artRates : []);
    const matched = candidates.find((entry) => {
      const candidateArtId = Number(entry?.unterrichtsform_art_id ?? entry?.art_id ?? 0);
      return candidateArtId === artId;
    });
    if (!matched) return null;
    const ratePerHourEuro = Number(matched.rate ?? 0);
    if (!Number.isFinite(ratePerHourEuro) || ratePerHourEuro <= 0) return null;
    const ratePerHourCents = Math.round(ratePerHourEuro * 100);
    return Math.round(ratePerHourCents * (lessonDurationMin / 60));
  }, [currentForm, lessonDurationMin, teacherPerson]);

  const honorarPerLessonCents = useMemo(() => {
    if (typeof teacherArtRatePerLessonCents === 'number' && teacherArtRatePerLessonCents > 0) {
      return teacherArtRatePerLessonCents;
    }
    return null;
  }, [teacherArtRatePerLessonCents]);

  const gesamthonorarCents = honorarPerLessonCents !== null && packageUnits !== null
    ? honorarPerLessonCents * packageUnits
    : null;
  const discountPercent = typeof enrollment?.discountPercent === 'number' ? enrollment.discountPercent : 0;
  const totalAfterDiscount = gesamthonorarCents !== null
    ? Math.round(gesamthonorarCents * (1 - Math.max(0, discountPercent) / 100))
    : null;
  const unterrichtsformName = useMemo(() => {
    const id = enrollment?.unterrichtsformId;
    if (typeof id !== 'number') return 'Nicht festgelegt';
    const hit = unterrichtsformen.find((u) => u.id === id);
    return hit?.name || `Unterrichtsform #${id}`;
  }, [enrollment?.unterrichtsformId, unterrichtsformen]);
  const paymentPill = loadingPaymentReadiness
    ? { label: 'Prüfung läuft', className: 'border-slate-200 bg-slate-50 text-slate-700' }
    : paymentReadiness
      ? (paymentReadiness.canChargeNow
        ? { label: 'Belastbar', className: 'border-green-200 bg-green-50 text-green-700' }
        : { label: 'Nicht belastbar', className: 'border-red-200 bg-red-50 text-red-700' })
      : paymentStatusPill(enrollment?.paymentStatus, enrollment?.paymentChargeState);
  const paymentIsGreen = paymentReadiness?.canChargeNow ?? isPaymentStatusGreen(enrollment?.paymentStatus, enrollment?.paymentChargeState);
  const paymentMethodDisplay = paymentReadiness?.method?.label || paymentMethodLabel(enrollment?.stripePaymentMethodId);
  const paymentReadinessError = paymentReadiness?.errors?.[0] || null;
  const simulationStatusLabel = loadingPaymentReadiness
    ? 'Wird geprüft'
    : paymentReadiness?.simulation?.performed
      ? (paymentReadiness.simulation.ok ? 'Bestanden' : 'Fehlgeschlagen')
      : 'Nicht durchgeführt';
  const startDateValue = enrollment?.startDate || trial.date;
  const startTimeValue = enrollment?.startTime || trial.startTime;
  const endTimeValue = enrollment?.durationMin
    ? addMinutesToTime(startTimeValue, enrollment.durationMin)
    : toTimeLabel(trial.endTime);
  const terminWeekday = weekdayForTermin(enrollment?.weekday, startDateValue);
  const terminLabel = `${terminWeekday ? `${terminWeekday}, ` : ''}${toTimeLabel(startTimeValue)} - ${endTimeValue || '-'} Uhr`;
  const plannedPeriod = useMemo(
    () => computePlannedPeriodRange({
      startDate: startDateValue,
      lessonCount: packageUnits,
      intervalWeeks: enrollment?.intervalWeeks ?? 1,
    }),
    [startDateValue, packageUnits, enrollment?.intervalWeeks],
  );
  const weekdayDisplayName = weekdayNameFromNumber(enrollment?.weekday) || terminWeekday.replace(/s$/, '');

  const contractPartnerContact = useMemo(() => {
    const subprofileId = String((trial as any)?.subprofileId || enrollment?.subprofileId || '').trim();
    if (!subprofileId.includes('-')) {
      return { name: trial.studentName || '—', email: trial.contactEmail || '—' };
    }
    const wpUserId = Number.parseInt(subprofileId.split('-', 2)[0], 10);
    if (wpUserId <= 0) {
      return { name: trial.studentName || '—', email: trial.contactEmail || '—' };
    }
    const vpResolved = resolvedContacts[`person:${wpUserId}:`] || resolvedContacts[`person:${wpUserId}:vp`];
    return {
      name: vpResolved?.name || trial.studentName || '—',
      email: vpResolved?.email || trial.contactEmail || '—',
    };
  }, [enrollment?.subprofileId, resolvedContacts, trial]);

  const orderEndLabel = packageUnits !== null
    ? `${packageUnits} Einheiten ab ${toDateLabel(startDateValue)}`
    : '—';

  const historyTemplateVars = useMemo(() => ({
    orderId,
    studentName: trial.studentName || '',
    teacherName: trial.teacherName || 'Lehrer',
    fachName: trial.fachName || trial.instrument || '',
    unterrichtsformName,
    startDate: startDateValue || '',
    startTime: toTimeLabel(startTimeValue),
    weekdayName: weekdayDisplayName,
    roomName: trial.roomName || '',
    honorar: toMoney(honorarPerLessonCents, currency),
    contractPartnerName: contractPartnerContact.name,
    contractPartnerEmail: contractPartnerContact.email,
  }), [
    orderId,
    trial,
    unterrichtsformName,
    startDateValue,
    startTimeValue,
    weekdayDisplayName,
    honorarPerLessonCents,
    currency,
    contractPartnerContact,
  ]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadHistoryPreview = async () => {
      try {
        const rendered: RenderHistoryTemplateResponse = await historyTemplatesApi.render({
          template_key: HISTORY_TEMPLATE_KEY,
          standort_id: standortId || undefined,
          vars: historyTemplateVars,
        });
        if (cancelled) return;
        setHistoryHtml(rendered.history || '');
      } catch {
        if (!cancelled) {
          setHistoryHtml(`Auftrag #${orderId} bestätigt für ${trial.studentName || 'Schüler'}.`);
        }
      }
    };
    void loadHistoryPreview();
    return () => {
      cancelled = true;
    };
  }, [open, historyTemplateVars, orderId, trial.studentName, standortId]);

  const handleNext = () => setStep((prev) => (prev < 3 ? ((prev + 1) as 1 | 2 | 3) : prev));
  const handleBack = () => setStep((prev) => (prev > 1 ? ((prev - 1) as 1 | 2 | 3) : prev));

  const sendComposerMessages = async (): Promise<void> => {
    if (!standortId || recipientGroups.length === 0) return;

    for (const group of recipientGroups) {
      const draft = draftStateByGroup[group.key];
      if (!draft) continue;

      const recipients: CommunicationsRecipient[] = group.recipients.map((r) =>
        r.type === 'person'
          ? { type: 'person', wpUserId: r.wpUserId, subIndex: r.subIndex ?? null }
          : { type: 'trial_request', trialRequestId: r.trialRequestId },
      );
      if (!recipients.length) continue;

      // E-Mails laufen im Confirm-Backend, damit Lehrer-PDF zuverlässig angehängt wird.
      if (draft.selectedChannels.sms) {
        await communicationsApi.send({
          channel: 'sms',
          standortId,
          recipients,
          draft: {
            smsTpl: draft.sms.smsTpl,
          },
          vars: group.vars,
        });
      }
    }
  };

  const shouldSendEmailForGroup = React.useCallback((groupKey: string): boolean => {
    const group = recipientGroups.find((entry) => entry.key === groupKey);
    if (!group) return false;
    const draft = draftStateByGroup[groupKey];
    if (draft) return Boolean(draft.selectedChannels.email);
    return Boolean(group.defaultSelectedChannels?.email);
  }, [draftStateByGroup, recipientGroups]);

  const collectStudentEmails = React.useCallback((): string[] => {
    if (!shouldSendEmailForGroup('student')) return [];
    const group = recipientGroups.find((entry) => entry.key === 'student');
    if (!group) return [];
    const emails = group.recipients
      .map((recipient) => {
        const resolved = resolvedContacts[recipient.id]
          || resolvedContacts[`person:${recipient.wpUserId}:${recipient.subIndex ?? ''}`.replace(/:$/, ':')];
        return (resolved?.email || '').trim();
      })
      .filter(Boolean);
    return [...new Set(emails)];
  }, [recipientGroups, resolvedContacts, shouldSendEmailForGroup]);

  const handleConfirm = async () => {
    if (!orderId) {
      toast.error('Kein Auftrag vorhanden');
      return;
    }

    if (!paymentIsGreen) {
      const proceed = globalThis.confirm(
        'Die Zahlung ist aktuell nicht als aktiv markiert. Möchtest du den Auftrag trotzdem bestätigen?'
      );
      if (!proceed) {
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await sendComposerMessages();
      await confirmOrder(orderId, {
        sendTeacherEmail: shouldSendEmailForGroup('teacher'),
        sendStudentEmail: shouldSendEmailForGroup('student'),
        studentEmails: collectStudentEmails(),
        // Die im Editor bearbeiteten Mails. Bisher schickte das Backend feste
        // Sätze statt course.confirmed.* (T-0071).
        drafts: entwuerfe(),
        history: historyHtml.trim()
          ? { type: 'order.confirmed', description: historyHtml.trim() }
          : undefined,
        // Im Probestunden-Bereich startet das Bestätigen den Unterricht gleich mit;
        // „Unterricht starten“ als zweiter Ablauf verwirrte (T-0293, T-0318).
        // Migrationsfälle starten den Kurs weiter in einem eigenen Schritt.
        startCourseEnrollmentId: !enrollmentIdOverride && enrollment?.id ? Number(enrollment.id) : undefined,
      });
      await onConfirmed?.();
      toast.success(
        !enrollmentIdOverride && enrollment?.id ? 'Auftrag bestätigt, Unterricht gestartet' : 'Auftrag wurde bestätigt'
      );
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Auftrag konnte nicht bestätigt werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComposerDraftsChange = React.useCallback(({ groups }: { groups: Record<string, GroupDraftState> }) => {
    const signature = JSON.stringify(groups ?? {});
    if (signature === lastDraftSignatureRef.current) return;
    lastDraftSignatureRef.current = signature;
    setDraftStateByGroup(groups ?? {});
  }, []);

  const notificationSummary = useMemo(() => recipientGroups.map((group) => {
    const draft = draftStateByGroup[group.key];
    const emailSelected = draft?.selectedChannels.email ?? group.defaultSelectedChannels?.email ?? true;
    const smsSelected = draft?.selectedChannels.sms ?? group.defaultSelectedChannels?.sms ?? false;
    return { group, emailSelected, smsSelected };
  }), [draftStateByGroup, recipientGroups]);

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Auftrag bestätigen"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Auftragsvorschau' }, { label: 'Kommunikation' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? handleBack : undefined}
      canBack={!isSubmitting}
      onNext={step < 3 ? handleNext : undefined}
      canNext={canNext && !isSubmitting}
      onPrimary={step === 3 ? handleConfirm : undefined}
      primaryLabel={isSubmitting ? 'Wird bestätigt…' : 'Bestätigen'}
      canPrimary={canConfirm}
      isLoading={isSubmitting}
    >
      <div className="py-4 space-y-4">
        {step === 1 && (
          loadingEnrollment ? (
            <WizardCard>
              <WizardSection title="Auftragsbestätigung (Vorschau)">
                <div className="rounded-md border bg-muted/30 p-8 text-sm text-muted-foreground">
                  Auftragsdaten werden geladen…
                </div>
              </WizardSection>
            </WizardCard>
          ) : (
          <WizardCard>
            <WizardSection title="Auftragsbestätigung (Vorschau)">
              <div className="rounded-md border bg-white p-8 text-black shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-2xl font-bold leading-none">FIT IN music</div>
                    <div className="mt-1 text-base font-semibold">Deine Musikschule</div>
                    <div className="mt-5 text-[12px] leading-5">
                      FIT IN music GmbH &amp; Co. KG · Jasperallee 33 · 38102 Braunschweig
                    </div>
                  </div>
                  <div className="text-right text-[13px] leading-6">
                    <div>
                        Braunschweig, den {format(new Date(), 'dd.MM.yyyy', { locale: de })}
                    </div>
                  </div>
                </div>

                <div className="mt-5 text-[14px] leading-6">
                  <div>{trial.teacherName || 'Lehrer'}</div>
                  <div>Adressdaten aus Lehrerprofil</div>
                </div>

                <div className="mt-7">
                  <div className="text-[37px] font-semibold leading-tight tracking-tight">Auftragsbestätigung</div>
                  <div className="text-[20px] font-semibold leading-tight">
                    {orderId > 0 ? `Auftrag Nr. ${orderId}` : 'Auftrag'}
                  </div>
                </div>

                <div className="mt-7 border-b border-black pb-2 text-[17px] leading-8">
                  <div>Hallo {(trial.teacherName || 'Lehrer').split(' ')[0]},</div>
                  <div>hiermit bestätigen wir folgenden Auftrag:</div>
                </div>

                <div className="mt-4 border border-black/70">
                  {[
                    ['Schüler/in', trial.studentName || '—'],
                    ['Unterrichtsform', unterrichtsformName],
                    ['Anzahl Termine', packageUnits !== null ? String(packageUnits) : '—'],
                    ['Stundensatz', toMoney(stundensatzAus(honorarPerLessonCents, lessonDurationMin), currency)],
                    ['Honorar je Termin', toMoney(honorarPerLessonCents, currency)],
                    ['Gesamtbetrag', toMoney(totalAfterDiscount, currency)],
                    ['Eingeplanter Zeitraum', plannedPeriod.label],
                    ['Termin', terminLabel],
                  ].map(([label, value]) => (
                    <div key={label} className="grid grid-cols-[280px_1fr] border-b border-black/40 text-[14px] leading-6 last:border-b-0">
                      <div className="border-r border-black/40 px-3 py-1.5 font-semibold">{label}</div>
                      <div className="px-3 py-1.5 whitespace-pre-line">{value}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-7 border-t border-black pt-3 text-[14px] leading-6">
                  <div className="font-semibold">Zusatz</div>
                  <div className="mt-2">
                    Die Parteien beabsichtigen, die Zusammenarbeit über den vereinbarten Leistungszeitraum hinaus fortzusetzen.
                    Weitere Einsätze erfolgen jeweils durch gesonderte Beauftragung. Ein Anspruch auf Beauftragung besteht nicht.
                  </div>
                </div>

                {/* UNT-1: Auftragsabschluss — gehört in die Auftragsbestätigung
                    (analog OrderDocumentService::renderClosure). Konkrete Daten
                    werden beim Bestätigen vom OrderService befüllt. */}
                <div className="mt-7 border-t border-black pt-3">
                  <div className="text-[16px] font-semibold">Auftragsabschluss</div>
                  <div className="mt-2 grid grid-cols-2 gap-8 text-[14px] leading-6">
                    <div>
                      <div className="font-semibold">Auftraggeber</div>
                      {/* Wer den Auftrag erteilt, im Auftrag der Schule (T-0281). */}
                      <div>{user?.name ? `i. A. ${user.name}` : 'Office-Team'}</div>
                      <div>FIT IN music GmbH &amp; Co. KG</div>
                    </div>
                    <div>
                      <div className="font-semibold">Auftragnehmer</div>
                      <div>{trial.teacherName || 'Lehrer'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </WizardSection>
            <WizardSection title="Zahlungsdetails">
              <WizardKVGrid>
                <WizardKVItem label="Anmeldung">{enrollment?.id ? `#${enrollment.id}` : '-'}</WizardKVItem>
                <WizardKVItem label="Zahlungsanbieter">{paymentProviderLabel(enrollment?.paymentProvider)}</WizardKVItem>
                <WizardKVItem label="Zahlungsart">{paymentMethodDisplay}</WizardKVItem>
                <WizardKVItem label="Zahlungsstatus">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${paymentPill.className}`}>
                    {paymentPill.label}
                  </span>
                </WizardKVItem>
                <WizardKVItem label="Fällig am">
                  {toDateLabel(startDateValue)}
                </WizardKVItem>
                <WizardKVItem label="Belastungstest">{simulationStatusLabel}</WizardKVItem>
                {paymentReadinessError ? (
                  <WizardKVItem label="Prüfhinweis">
                    {paymentReadinessError}
                  </WizardKVItem>
                ) : null}
                {enrollment?.paymentChargeError ? (
                  <WizardKVItem label="Fehlergrund">
                    {enrollment.paymentChargeError}
                  </WizardKVItem>
                ) : null}
              </WizardKVGrid>
            </WizardSection>
            {enrollment?.einwilligungen ? (
              <WizardSection title="Einwilligungen">
                <p className="text-sm">{einwilligungenText(enrollment.einwilligungen)}</p>
              </WizardSection>
            ) : null}
          </WizardCard>
          )
        )}

        {step === 2 && (
          <div className="space-y-3">
            {standortId > 0 && recipientGroups.length > 0 ? (
              <CommunicationComposer
                active={open && step === 2}
                standortId={standortId}
                groups={recipientGroups}
                onDraftsChange={handleComposerDraftsChange}
              />
            ) : (
              <div className="rounded-lg border p-3 text-sm text-muted-foreground">
                Kommunikation ist für diesen Datensatz nicht verfügbar (fehlender Standort oder Empfänger).
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <WizardCard>
            <WizardSection title="Vertrag">
              <WizardKVGrid>
                <WizardKVItem label="Vertragspartner">{contractPartnerContact.name}</WizardKVItem>
                <WizardKVItem label="E-Mail Vertragspartner">{contractPartnerContact.email}</WizardKVItem>
                <WizardKVItem label="Schüler">{trial.studentName || '—'}</WizardKVItem>
                <WizardKVItem label={getSubjectLabels().singular}>{trial.fachName || trial.instrument || '—'}</WizardKVItem>
                <WizardKVItem label="Unterrichtsform">{unterrichtsformName}</WizardKVItem>
                <WizardKVItem label="Termin">{terminLabel}</WizardKVItem>
                <WizardKVItem label="Raum">{trial.roomName || '—'}</WizardKVItem>
                <WizardKVItem label="Eingeplanter Zeitraum">{plannedPeriod.label}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Auftrag">
              <WizardKVGrid>
                <WizardKVItem label="Auftragsnummer">{orderId > 0 ? `#${orderId}` : '—'}</WizardKVItem>
                <WizardKVItem label="Status">{trial.order?.status || '—'}</WizardKVItem>
                <WizardKVItem label="Lehrer">{trial.teacherName || '—'}</WizardKVItem>
                <WizardKVItem label="Geplantes Auftragsende">{orderEndLabel}</WizardKVItem>
                <WizardKVItem label="Anzahl Termine">{packageUnits !== null ? String(packageUnits) : '—'}</WizardKVItem>
                <WizardKVItem label="Honorar (pro Termin)">{toMoney(honorarPerLessonCents, currency)}</WizardKVItem>
                <WizardKVItem label="Gesamtbetrag">{toMoney(totalAfterDiscount, currency)}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                {notificationSummary.map(({ group, emailSelected, smsSelected }) => (
                  <WizardKVItem key={group.key} label={group.label}>
                    <div className="space-y-2">
                      {group.recipients.map((recipient) => {
                        const resolved = resolvedContacts[recipient.id]
                          || resolvedContacts[`person:${recipient.wpUserId}:${recipient.subIndex ?? ''}`.replace(/:$/, ':')];
                        const displayName = resolved?.name?.trim() || recipient.name;
                        return (
                          <div key={recipient.id} className="flex flex-wrap items-center gap-2">
                            <span>{displayName}</span>
                            <span className={channelPillClass(emailSelected)}>E-Mail</span>
                            <span className={channelPillClass(smsSelected)}>SMS</span>
                          </div>
                        );
                      })}
                    </div>
                  </WizardKVItem>
                ))}
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Historie">
              <div className="space-y-2">
                <Label>Historieneintrag</Label>
                <Textarea
                  value={historyHtml}
                  onChange={(e) => setHistoryHtml(e.target.value)}
                  className="min-h-[110px]"
                />
              </div>
            </WizardSection>
          </WizardCard>
        )}
      </div>
    </WizardShell>
  );
}

